import { getApi } from 'common/modules/ReactShared/data/api';
import type {
  TestsFetchResponse,
  TestItem,
  TestResult,
  TestResultKind,
  TestStatus,
  tStudentExamination,
  tStudentExaminationsResponse,
  tStudentExaminationStatus
} from './types';

const _mapExaminationStatus = (status: tStudentExaminationStatus): TestStatus => {
  if (status === 'pending') return 'assigned';
  return status;
};

const RESULT_KINDS: ReadonlySet<TestResultKind> = new Set<TestResultKind>([
  'placement',
  'pass_fail',
  'level',
  'score',
  'awaiting_review',
  'none'
]);

// The server decides what a test concluded; this only guards against a kind a
// newer server adds before the app knows how to draw it, which shows nothing
// rather than something wrong.
export const mapExaminationResult = (examination: tStudentExamination): TestResult => {
  const { outcome } = examination;
  if (outcome && RESULT_KINDS.has(outcome.kind)) return outcome;
  return { kind: 'none' };
};

const _mapExaminationToTestItem = (examination: tStudentExamination): TestItem => ({
  id: examination.id,
  name: examination.title,
  language: examination.locale,
  status: _mapExaminationStatus(examination.status),
  dueDate: examination.expiresAt,
  totalTimeSpent: examination.totalTimeSpent,
  totalScore: examination.totalScore,
  maxScore: examination.maxScore,
  completedAt: examination.completedAt,
  url: examination.url,
  resultUrl: examination.resultURL ?? null,
  result: mapExaminationResult(examination)
});

interface FetchTestsParams {
  page?: number;
  per_page?: number;
  [key: string]: unknown;
}

async function fetchTests(
  path: string,
  params?: FetchTestsParams
): Promise<TestsFetchResponse> {
  const { data } = await getApi().get<tStudentExaminationsResponse>(path, { params });
  return {
    records: data.examinations.map(_mapExaminationToTestItem),
    totalCount: data.totalCount,
    meta: data.meta ?? null
  };
}

export function fetchInProgressTests(
  params?: FetchTestsParams
): Promise<TestsFetchResponse> {
  return fetchTests('/examinations/in_progress', params);
}

export function fetchPendingTests(
  params?: FetchTestsParams
): Promise<TestsFetchResponse> {
  return fetchTests('/examinations/pending', params);
}

export function fetchCompletedTests(
  params?: FetchTestsParams
): Promise<TestsFetchResponse> {
  return fetchTests('/examinations/completed', params);
}

export * from './types';
